import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {merge, Observable, Subscription} from 'rxjs';
import {FilterOptionModel} from './filter-option.model';
import {distinctUntilChanged, map, startWith, tap} from 'rxjs/operators';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import {FocusMonitor} from '@angular/cdk/a11y';
import {SelectedFilterValuesModel} from './selected-filter-values.model';
import {PhoneNumberPipe} from '../../core/pipes/phone-number.pipe';
import {PhoneNumbersUtils} from '../../core/utilities/phone-numbers-helpers';
import {ActivatedRoute, Router} from '@angular/router';
import {compressToEncodedURIComponent, decompressFromEncodedURIComponent} from 'lz-string';
import {DateRangeService} from '../../core/services/date-range.service';
import {GridSortService} from '../../core/services/grid-sort.service';
import {Sort} from '@angular/material/sort';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatFormFieldFix } from '../../editors/mat-form-field/mat-form-field-fix';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatDateRangeComponent } from '../mat-date-range/mat-date-range.component';
import { MatButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';

@Component({
    selector: 'app-grid-filter',
    templateUrl: './grid-filter.component.html',
    styleUrls: ['./grid-filter.component.scss'],
    standalone: true,
    imports: [FlexModule, NgIf, MatFormFieldFix, MatFormField, MatIcon, MatPrefix, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, MatAutocomplete, NgFor, MatOption, MatDateRangeComponent, MatButton, MatChipListbox, MatChipOption, MatChipRemove, AsyncPipe]
})
export class GridFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Output() filterChangeEvent = new EventEmitter<any>();
  @Output() onFilterButtonClicked = new EventEmitter<any>();
  @Output() onInitialized = new EventEmitter<any>();
  @Input() placeholder: string;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() hideSearchInput = false;
  @Input() showFilterButton = false;
  @Input() showDateRange = false;
  @Input() defaultDateRange = 0;
  @Input() filterOptions: FilterOptionModel[] = [];
  @Input() disableUrlChanges = false;
  @Input() initialQuery = '';
  currentQuery = '';

  selectedSort: Sort;
  selectedItems: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  @Output() selectedItemsChanged = new EventEmitter<SelectedFilterValuesModel[]>();
  @Input() selectedItemsChange = new EventEmitter<SelectedFilterValuesModel[]>();


  items: SelectedFilterValuesModel[] = [];
  phoneNumberPipe = new PhoneNumberPipe();

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;
  focused = false;
  lastFilter = '';
  searchControl = new UntypedFormControl();
  filteredOptions: Observable<FilterOptionModel[]>;


  paramsSubscription: Subscription;
  sortSubscription: Subscription;
  monitorSubscription: Subscription;
  selectedItemsSubscription: Subscription;
  mergeSubscription: Subscription;

  filterTimeout: any;

  constructor(
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    private dateRangeService: DateRangeService,
    private gridSortService: GridSortService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.monitorSubscription = fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes);

    if (changes['initialQuery']) {
      this.currentQuery = changes['initialQuery'].currentValue;

      if (!this.initialQuery) {
        this.selectedItems = [];
      }
    }

    if (this.initialQuery) {
      console.log('this.initialQuery', this.initialQuery);
      this.appendQueryFilter(this.initialQuery);
    }

    const queryParams = this.route.snapshot.queryParams;

    if (queryParams['s']) {
      const currentSort = decompressFromEncodedURIComponent(queryParams['s']);
      console.log('paramsSubscription', currentSort);

      this.appendQuerySort(currentSort);
    }

    if (queryParams['q']) {
      this.currentQuery = queryParams['q'];
      const currentQuery = decompressFromEncodedURIComponent(queryParams['q']);
      console.log('currentQuery', currentQuery);
      this.appendQueryFilter(currentQuery);
    } else {
      if (changes['defaultDateRange'] && !queryParams['q']) {
        this.dateRangeService.selectDateRangeByType(this.defaultDateRange);
      } else if (changes['filterOptions']) {
        this.onFilterChange(this.selectedItems, this.selectedSort);
      }
    }
  }

  appendQuerySort(queryStr?: string | null) {
    if (!queryStr) {
      return;
    }

    const sort = JSON.parse(queryStr);
    const currentSort = this.gridSortService.gridSortSubject.getValue();
    console.log('sort', sort);
    this.selectedSort = sort;

    if (!currentSort || currentSort.active !== sort.active || currentSort.direction !== sort.direction) {
      this.gridSortService.gridSortSubject.next(this.selectedSort);
    }
  }

  appendQueryFilter(queryStr?: string | null) {
    if (!queryStr) {
      this.selectedItems = [];
      return;
    }

    const newFilter: SelectedFilterValuesModel[] = [];
    try {
      if (queryStr) {
        const filter = JSON.parse(queryStr);
        for (const filterKey of Object.keys(filter)) {
          const availableFilterOption = this.filterOptions.find(value => value.filterKey === filterKey);
          if (availableFilterOption) {
            newFilter.push(
              {
                filterKey: availableFilterOption.filterKey,
                multiValue: availableFilterOption.multiValue,
                title: availableFilterOption.title,
                values: filter[filterKey].values,
                displayValues: filter[filterKey].displayValues,
                operator: availableFilterOption.operator ?? 'equals'
              }
            );
          }

          if (filterKey === 'dateRange') {
            this.dateRangeService.selectDateRangeByType(filter[filterKey].action, filter[filterKey].startDate, filter[filterKey].endDate);
          }
        }
      }
    } catch (e) {
      console.warn('unable to parse query filter', e);
    }
    this.selectedItems = newFilter;
    if (this.filterOptions.length > 0) {
      this.onFilterChange(this.selectedItems, this.selectedSort);
    }
  }

  ngOnInit() {
    this.paramsSubscription = this.route.queryParams.subscribe(async (queryParams) => {
      if (this.disableUrlChanges) {
        this.onInitialized.emit();
        return;
      }

      console.log('paramsSubscription', queryParams, this.currentQuery);
      if (queryParams['s']) {
        const currentSort = decompressFromEncodedURIComponent(queryParams['s']);
        console.log('paramsSubscription', currentSort);

        this.appendQuerySort(currentSort);
      }

      if (queryParams['q'] && this.currentQuery !== queryParams['q']) {
        const currentQuery = decompressFromEncodedURIComponent(queryParams['q']);
        console.log('paramsSubscription', currentQuery);
        this.appendQueryFilter(currentQuery);
      }

      this.currentQuery = queryParams['q'];

      if (!queryParams['q'] && this.filterOptions.length > 0) {
        this.selectedItems = [];
        this.onFilterChange(this.selectedItems, this.selectedSort);
      }

      this.onInitialized.emit();
    });

    this.filteredOptions = this.searchControl.valueChanges
      .pipe(
        map(value => typeof value === 'string' ? value : this.lastFilter),
        startWith(''),
        map(value => this.filter(value))
      );

    this.selectedItemsSubscription = this.selectedItemsChange.subscribe((value) => {
      console.log('selectedItemsChange', value);
      this.selectedItems = value;
      // this.onFilterChange(this.selectedItems, this.selectedSort);
    });

    this.sortSubscription = this.gridSortService.gridSortSubject.subscribe(sort => {
      console.log('new sort', sort);
      this.selectedSort = sort;
      // this.onFilterChange(this.selectedItems, this.selectedSort);
    });

    this.mergeSubscription = merge(this.selectedItemsChange, this.gridSortService.gridSortSubject)
      .pipe(
        tap(() => {
          if (this.filterOptions.length > 0) {
            this.onFilterChange(this.selectedItems, this.selectedSort);
          }
        })
      )
      .subscribe();
  }

  filter(searchText: string): FilterOptionModel[] {
    let filterValue = searchText?.toLowerCase();
    console.log('filter data: ' + filterValue);
    if (filterValue.startsWith('/')) {
      filterValue = filterValue.replace('/', '');
    }

    let filteredItems =
      this.filterOptions
        .filter(option => (option.searchText || '').toLowerCase().includes(filterValue) || option.showAlways)
        .map(x => Object.assign({}, x)); // copy options reference

    const possiblePhoneNumberText = this.phoneNumberPipe.transform(filterValue, true);
    const filterOutPhoneType = (filterValue && !possiblePhoneNumberText) || possiblePhoneNumberText.trim() === '(invalid)';
    const filterOutNumeric = filterValue && isNaN(Number(filterValue));
    const filterOutDate = filterValue && isNaN(Date.parse(filterValue));

    if (filterOutPhoneType) {
      filteredItems = filteredItems.filter(option => option.type !== 'phoneNumber');
    }
    if (filterOutNumeric) {
      filteredItems = filteredItems.filter(option => option.type !== 'number');
    }
    if (filterOutDate) {
      filteredItems = filteredItems.filter(option => option.type !== 'date');
    }

    for (const filteredItem of filteredItems) {
      if (filteredItem.type === 'string') {
        filteredItem.displayValue = filterValue;
        filteredItem.value = filterValue;
        filteredItem.searchText = filterValue;
      } else if (filteredItem.type === 'phoneNumber') {
        filteredItem.displayValue = possiblePhoneNumberText;
        filteredItem.value = !possiblePhoneNumberText.includes('(invalid)')
          ? PhoneNumbersUtils.phoneNumberToNational(possiblePhoneNumberText) : filterValue;
        filteredItem.searchText = !possiblePhoneNumberText.includes('(invalid)')
          ? PhoneNumbersUtils.phoneNumberToNational(possiblePhoneNumberText) : filterValue;
      } else if (filteredItem.type === 'number') {
        filteredItem.displayValue = filterValue;
        filteredItem.value = filterValue;
        filteredItem.searchText = filterValue;
      } else if (filteredItem.type === 'date') {
        filteredItem.displayValue = filterValue;
        filteredItem.value = filterValue;
        filteredItem.searchText = filterValue;
      }
    }

    filteredItems.sort((a, b) => {
        if (a.filterKey == 'searchText' || b.filterKey == 'searchText') {
          return 1;
        }

        if (a.type !== b.type) {
          if (a.type == 'phoneNumber' || b.type == 'phoneNumber') {
            return 1;
          }
        }

        return (a.searchText > b.searchText) ? -1 : 1;
      }
    );

    filteredItems.sort((a, b) => {
        if (a.filterKey == 'searchText' || b.filterKey == 'searchText') {
          return 1;
        }
        return (a.showAlways > b.showAlways) ? 1 : -1;
      }
    );

    return filteredItems;
  }

  ngOnDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.fm.stopMonitoring(this.elRef.nativeElement);
    this.paramsSubscription?.unsubscribe();
    this.sortSubscription?.unsubscribe();
    this.monitorSubscription?.unsubscribe();
    this.selectedItemsSubscription?.unsubscribe();
    this.mergeSubscription?.unsubscribe();
  }

  onDateRangeSelected(range: any) {
    console.log(range);
    this.onFilterChange(this.selectedItems, this.selectedSort);
  }

  optionSelected($event: MatAutocompleteSelectedEvent) {
    console.log('select filter', $event);
    const option: FilterOptionModel = $event.option.value;
    const selectedFilter = this.selectedItems.find(value => value.filterKey === option.filterKey);

    if (!selectedFilter) {
      this.selectedItems.push(
        {
          filterKey: option.filterKey,
          multiValue: option.multiValue,
          title: option.title,
          values: [option.value],
          displayValues: [option.displayValue],
          operator: option.operator ?? 'equals'
        }
      );
    } else {
      if (!selectedFilter.multiValue) {
        selectedFilter.displayValues = [option.displayValue];
        selectedFilter.values = [option.value];
      }
      if (!selectedFilter.values.includes(option.value)) {
        selectedFilter.values.push(option.value);
      }
      if (!selectedFilter.displayValues.includes(option.displayValue)) {
        selectedFilter.displayValues.push(option.displayValue);
      }
    }

    this.clearInput();
    this.onFilterChange(this.selectedItems, this.selectedSort);
  }

  clearInput() {
    this.inputTrigger.nativeElement.value = '';
    this.searchControl.setValue(null);
  }

  removeFilter(item: SelectedFilterValuesModel): void {
    const index = this.selectedItems.findIndex(value => value.filterKey === item.filterKey);
    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.onFilterChange(this.selectedItems, this.selectedSort);
    }
  }

  onFilterChange(filter: SelectedFilterValuesModel[], sort: Sort) {
    console.log('onFilterChange', filter);
    this.selectedItems = filter;
    this.selectedItemsChanged.emit(this.selectedItems);

    const queryObject: any = {};
    const filterObject: any = {};

    for (const filterOption of filter) {
      filterObject[filterOption.filterKey] = filterOption.multiValue ? filterOption.values : filterOption.values[0];
      queryObject[filterOption.filterKey] = {
        values: filterOption.values,
        displayValues: filterOption.displayValues
      };
    }

    if (this.showDateRange) {
      filterObject.startDate = this.dateRangeService.rangeStartMoment.toUTC().toISO();
      filterObject.endDate = this.dateRangeService.rangeEndMoment.toUTC().toISO();

      queryObject.dateRange = {
        action: this.dateRangeService.selectedDateRange.action,
        startDate: this.dateRangeService.rangeStartMoment.toUTC().toISO(),
        endDate: this.dateRangeService.rangeEndMoment.toUTC().toISO(),
      };
    }

    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = setTimeout(() => {
      this.filterChangeEvent.emit(filterObject);
    });

    const routerOptions: any = {
      queryParams: {}
    };
    if (this.selectedItems.length > 0 || queryObject.dateRange) {
      const filterStr = JSON.stringify(queryObject);
      const filterQuery = compressToEncodedURIComponent(filterStr);
      routerOptions.queryParams.q = filterQuery;
      this.currentQuery = filterQuery;
    } else {
      routerOptions.queryParams.q = null;
      this.currentQuery = '';
    }
    if (this.selectedSort) {
      const sortStr = JSON.stringify(this.selectedSort);
      routerOptions.queryParams.s = compressToEncodedURIComponent(sortStr);
    }

    if ((this.filterOptions.length > 0 || queryObject.dateRange) && !this.disableUrlChanges) {
      this.router.navigate([], routerOptions)
        .catch((error) => console.log(error));
    }
  }
}
